import React, { useEffect, useMemo, useState } from "react";

const isLocal = typeof window !== "undefined" && window.location.hostname === "localhost";
const baseOrigin = isLocal ? "http://localhost:5000" : "https://www.trendscout.club";
const widgetUrl = `${baseOrigin}/metals-widgets/metals`;

// safer clipboard helper
async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  }
}

export default function MetalsEmbeds() {
  const [metal, setMetal] = useState("XPT");      // XPT|XPD|XRH|ALL|XPT,XPD
  const [theme, setTheme] = useState("dark");     // dark|light
  const [horizon, setHorizon] = useState("7d");   // 7d|30d
  const [compact, setCompact] = useState(true);   // show compact by default
  const [height, setHeight] = useState(420);
  const [width, setWidth] = useState('100%');     // NEW
  const [maxWidth, setMaxWidth] = useState('');

  const [iframeMsg, setIframeMsg] = useState("");
  const [scriptMsg, setScriptMsg] = useState("");

  useEffect(() => {
    // Suggest taller height for ALL
    if (metal.toUpperCase() === "ALL" && height < 520) setHeight(560);
  }, [metal]); // eslint-disable-line

  const copyThen = async (code, setMsg) => {
    const ok = await copyText(code);
    setMsg(ok ? "Copied!" : "Copy failed");
    setTimeout(() => setMsg(""), 1500);
  };

  const q = useMemo(() => {
    let qs = `${widgetUrl}?metal=${encodeURIComponent(metal)}&theme=${encodeURIComponent(theme)}&horizon=${encodeURIComponent(horizon)}`;
    if (compact) qs += `&compact=1`;
    return qs;
  }, [metal, theme, horizon, compact]);

  const iframeCode = useMemo(() => {
    return `<iframe
  src="${q}${maxWidth ? `&max=${encodeURIComponent(maxWidth)}` : ''}"
  style="border:0;width:${width};height:${height}px"
  loading="lazy"
  referrerpolicy="no-referrer-when-downgrade"
></iframe>`;
  }, [q, height, width, maxWidth]);

  const scriptCode = useMemo(() => {
    const scriptSrc = `${baseOrigin}/embed/metals.js`;
    const compactAttr = compact ? ' data-compact="1"' : '';
    return `<script src="${scriptSrc}"
  data-metal="${metal}"
  data-theme="${theme}"
  data-horizon="${horizon}"${compactAttr}
  data-width="${width}"
  data-height="${height}"${maxWidth ? ` data-maxwidth="${maxWidth}"` : ''}></script>`;
  }, [metal, theme, horizon, compact, height, width, maxWidth]);

  const previewSrc = q;

  return (
    <div className="embeds-wrap">
      <div className="embeds-head">
        <h2>Metals Widget Embeds</h2>
      </div>

      <div className="embeds-controls">
        <label>
          Metal
          <select value={metal} onChange={(e) => setMetal(e.target.value)}>
            <option value="XPT">Platinum (XPT)</option>
            <option value="XPD">Palladium (XPD)</option>
            <option value="XRH">Rhodium (XRH)</option>
            <option value="ALL">All (XPT + XPD + XRH)</option>
          </select>
        </label>

        <label>
          Horizon
          <select value={horizon} onChange={(e) => setHorizon(e.target.value)}>
            <option value="7d">7D</option>
            <option value="30d">30D</option>
          </select>
        </label>

        <label>
          Theme
          <select value={theme} onChange={(e) => setTheme(e.target.value)}>
            <option value="dark">Dark</option>
            <option value="light">Light</option>
          </select>
        </label>

        <label>
          Compact
          <input
            type="checkbox"
            className="compact-metal-tick"
            checked={compact}
            onChange={(e) => setCompact(e.target.checked)}
          />
        </label>

        <label>
          Height (px)
          <input
            type="number"
            min={320}
            max={1200}
            value={height}
            onChange={(e) => setHeight(Number(e.target.value) || 420)}
          />
        </label>
        <label>
            Width (px or %)
            <input
              type="text"
              value={width}
              onChange={(e) => setWidth(e.target.value)}
              placeholder="e.g. 100% or 1024"
            />
          </label>

          <label>
            Max content width (optional)
            <input
              type="text"
              value={maxWidth}
              onChange={(e) => setMaxWidth(e.target.value)}
              placeholder="e.g. 1100, 1200px, or 90%"
            />
          </label>
      </div>

      <div className="embeds-grid">
        <div className="embed-card">
          <div className="embed-title">Live Preview</div>
          <div className="embed-preview">
            <iframe
              title="Metals Widget Preview"
              src={previewSrc}
              style={{ border: 0, width: "100%", height: `${height}px` }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>

        <div>
          <div className="embed-card">
            <div className="embed-title">Iframe Embed</div>
            <pre className="code-block"><code>{iframeCode}</code></pre>
            <button className="copy-btn" onClick={() => copyThen(iframeCode, setIframeMsg)}>Copy iframe</button>
            {iframeMsg && <span className="copy-msg">{iframeMsg}</span>}
          </div>

          <div className="embed-card">
            <div className="embed-title">Script Embed</div>
            <pre className="code-block"><code>{scriptCode}</code></pre>
            <button className="copy-btn" onClick={() => copyThen(scriptCode, setScriptMsg)}>Copy script</button>
            {scriptMsg && <span className="copy-msg">{scriptMsg}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
